import React, {ReactNode, useCallback, useEffect} from "react";
import '../../styles/common/modal.scss';
import {iconImages} from "../../config/config";
import {midiUserAction} from "../../services/midi/midi-user-action";
import {useAppSelector} from "../../redux/hooks";
import {settingsStore} from "../../redux/settings/settingsStore";
import PianoKey from "./PianoKey";

interface IModalProps {
  children?: ReactNode;
  onClose?: () => void;
  additionalClassName?: string;
  isFullScreen?: boolean;
  midiCloseShortcutNote?: "B";
}

export default function Modal({children, onClose, additionalClassName, isFullScreen, midiCloseShortcutNote}: IModalProps) {
  const isMidiDeviceConnected = useAppSelector(settingsStore.getIsMidiDeviceConnected);
  useEffect(() => {
    if (midiCloseShortcutNote && onClose) {
      return midiUserAction.subscribeToNextMidiNotePlayed(midiCloseShortcutNote, onClose);
    }
  }, [midiCloseShortcutNote, onClose]);

  const additionalClass = additionalClassName ? additionalClassName : '';
  const fullscreenClassName = isFullScreen? `fullscreen` : '';
  const midiCloseShortcutElement = midiCloseShortcutNote ? (
    <div className="navigation-key">
      <PianoKey pianoKey={'B'}/>
    </div>
  )
    : null;
  const midiShortcutIncludedClassName = midiCloseShortcutNote ? `midi-shortcut-included`: '';

  const closeOnEscapeKeypress = useCallback((event) => {
    if (event.key === 'Escape') {
      if (onClose) {
        onClose();
      }
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", closeOnEscapeKeypress);

    return () => {
      document.removeEventListener("keydown", closeOnEscapeKeypress);
    };
  }, [closeOnEscapeKeypress]);

  // onkeypress (onKeyPress in React) is officially deprecated, replaced in this case by onkeyup,
  // which is well-supported by browsers
  // @link https://developer.mozilla.org/en-US/docs/Web/API/Element/keypress_event
  // @link https://developer.mozilla.org/en-US/docs/Web/API/Element/keyup_event

  return (
    <div className={`modal ${fullscreenClassName} ${additionalClass}`}>
      <div className="modal-window" role="dialog">
        {onClose ?
          <button className={`close-button-and-midi-shortcut ${midiShortcutIncludedClassName}`} onClick={onClose} onKeyUp={closeOnEscapeKeypress}>
            <div className="close-button" aria-label="Close modal">
              <img alt={"Close"} src={iconImages.closeButton}/>
            </div>
            {isMidiDeviceConnected ? midiCloseShortcutElement : null}
          </button>
          :
          null
        }
        <div className="content">
          {children}
        </div>
      </div>
    </div>
  );
}
